import { useEffect, useRef, useState } from "react";
import type {
  ApiClient,
  SessionReplayGap,
  SseEvent,
  StreamState,
} from "../api";
import {
  normalizeSessionReplayGap,
  numberFrom,
  recordFrom,
  stringFrom,
} from "../api";

interface SessionStreamOptions {
  api: ApiClient;
  sessionId?: string;
  initialSeq: number;
  restartToken?: string | number;
  enabled: boolean;
  onEvent: (event: SseEvent) => void;
  onReplayGap?: (event: SessionReplayGap) => void;
}

export function useSessionStream({
  api,
  sessionId,
  initialSeq,
  restartToken,
  enabled,
  onEvent,
  onReplayGap,
}: SessionStreamOptions) {
  const [state, setState] = useState<StreamState>("idle");
  const [lastSeq, setLastSeq] = useState(initialSeq);
  const callbackRef = useRef(onEvent);
  const initialSeqRef = useRef(initialSeq);
  const replayGapCallbackRef = useRef(onReplayGap);
  initialSeqRef.current = initialSeq;

  useEffect(() => {
    callbackRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    replayGapCallbackRef.current = onReplayGap;
  }, [onReplayGap]);

  useEffect(() => {
    const epochInitialSeq = initialSeqRef.current;
    setLastSeq(epochInitialSeq);
    if (!enabled || !sessionId || !api.streamSupported) {
      setState("idle");
      return;
    }

    let disposed = false;
    let source: EventSource | undefined;
    let reconnectTimer: number | undefined;
    let reconnectAttempt = 0;
    let latestSeq = epochInitialSeq;

    const scheduleReconnect = (delay: number) => {
      if (reconnectTimer !== undefined) {
        window.clearTimeout(reconnectTimer);
      }
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposed) return;
      setState(reconnectAttempt ? "reconnecting" : "connecting");
      const currentSource = new EventSource(
        api.eventUrl(sessionId, latestSeq),
        {
          withCredentials: true,
        },
      );
      source = currentSource;

      currentSource.onopen = () => {
        reconnectAttempt = 0;
        setState("open");
      };

      currentSource.onmessage = (message) => {
        try {
          const raw = JSON.parse(message.data);
          const rawRecord = recordFrom(raw);
          if (stringFrom(rawRecord.type) === "session.replay_gap") {
            const replayGap = normalizeSessionReplayGap(raw, sessionId);
            if (!replayGap) {
              currentSource.close();
              setState("error");
              return;
            }
            latestSeq = replayGap.snapshot.lastSeq;
            setLastSeq(latestSeq);
            replayGapCallbackRef.current?.(replayGap);
            currentSource.close();
            reconnectAttempt = 0;
            setState("reconnecting");
            scheduleReconnect(0);
            return;
          }
          const event = normalizeSseEvent(raw, sessionId, latestSeq + 1);
          if (event.seq <= latestSeq) return;
          latestSeq = event.seq;
          setLastSeq(event.seq);
          callbackRef.current(event);
        } catch {
          setState("error");
        }
      };

      currentSource.onerror = () => {
        currentSource.close();
        if (disposed) return;
        if (reconnectTimer !== undefined) return;
        void api.getAuthSession().catch(() => undefined);
        reconnectAttempt += 1;
        setState("reconnecting");
        const delay = Math.min(1000 * 2 ** (reconnectAttempt - 1), 15000);
        scheduleReconnect(delay);
      };
    };

    connect();

    return () => {
      disposed = true;
      source?.close();
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
    };
  }, [api, enabled, restartToken, sessionId]);

  return { state, lastSeq };
}

function normalizeSseEvent(
  raw: unknown,
  sessionId: string,
  fallbackSeq: number,
): SseEvent {
  const record = recordFrom(raw);
  return {
    version: stringFrom(record.version) ?? numberFrom(record.version),
    event_id: stringFrom(record.event_id ?? record.id),
    seq: numberFrom(record.seq) ?? fallbackSeq,
    ts_ms: numberFrom(record.ts_ms ?? record.timestamp_ms),
    session_id: stringFrom(record.session_id) ?? sessionId,
    turn_id: stringFrom(record.turn_id),
    type: stringFrom(record.type ?? record.event_type) ?? "event",
    payload: recordFrom(record.payload ?? record.data),
  };
}
